import React, { useEffect, useState } from 'react';
import ApiLoading from '../APILoading';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { checkEmail, checkNumber, isPaymentTokenExpired } from 'utils/Utils';
import { defaultCountryList } from 'app/pages/Payment/country';
import useModals from '../Modal/manager/useModal';
import { usePaymentState } from 'payment_state/PaymentStateContext';
import ErrorModal from '../Modal/ErrorModal';
import { useGlobal } from 'global_state/useGlobal';
import { useMutation } from 'react-query';
import { authPv02, innerSearch } from 'api/Api';
import { RequestPV02Form } from './types';
import { Scheme } from 'app/pages/Payment/data';
import CustomModal from '../Modal/CustomModal';
import { isMobile } from 'react-device-detect';
import { useMediaQuery } from 'react-responsive';

interface PV02FormProps {
  selectedScheme: Scheme;
  openPrevious: () => void;
  setLoading: (method: boolean) => void;
  isGlobal: boolean;
}

const PV02Form = ({
  openPrevious,
  selectedScheme,
  setLoading,
  isGlobal,
}: PV02FormProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModals();
  const { paymentState, setPaymentState } = usePaymentState();
  const numberPattern = /^[0-9!@#$%^&*()\-=_+[\]{}|\\;:'",.<>/?]*$/;
  let checkPopupTimer;

  // 본인정보
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  // 청구서 주소
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [state, setState] = useState('');
  const [phoneNum, setPhoneNum] = useState('');
  const [email, setEmail] = useState('');

  const [isNameError, setNameError] = useState(false);
  const [isPhoneNumError, setPhoneNumError] = useState(false);
  const [isEmailError, setEmailError] = useState(false);

  const [countryList, setCountryList] = useState<
    { value: string; label: string }[]
  >([]);

  const [pv02RequestBody, setPv02RequestBody] = useState<RequestPV02Form>();
  const isMobileSize = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    setCountryList(
      defaultCountryList.map(item => ({ value: item, label: t(`${item}`) })),
    );
  }, [i18n.language]);

  // InnerSearch 실행
  const transactResult = useMutation(() => innerSearch(paymentState.tid), {
    onSuccess: data => {
      setLoading(false);

      // InnerSearch 결과처리
      payResult(data);

      // 결과 상태가 없을경우 예외처리
      if (
        data.data.response.resultStatus === null ||
        data.data.response.resultStatus === undefined
      ) {
        navigate('/error');
      }
    },
    onError: error => {
      setLoading(false);
      navigate('/error');
    },
  });

  // 결제 결과 PaymentState set 및 결과화면 처리
  const payResult = data => {
    // 결과 상태
    const resultStatus = String(data.data.response.resultStatus).toLowerCase();
    // 결제 타입
    const payType = String(data.data.display.payType).toLowerCase();

    // InnerSearch 결과 값 PaymentState 설정
    setPaymentState(prevState => ({
      ...prevState,
      resultCode: data.data.response.resultCode,
      resultMessage: data.data.response.resultMessage,
      tid: data.data.response.tid,
      orderId: data.data.response.orderId,
      mallReserved: data.data.response.mallReserved,
      // returnUrl: data.data.returnUrl,
      descriptor: data.data.display.descriptor,
      schemeGroup: data.data.response.schemeGroup,
      schemeCode: data.data.response.schemeCode,
    }));

    // Status에 따른 Paging 처리
    switch (resultStatus) {
      case 'success':
        navigate('/success');
        break;
      case 'decline':
        navigate('/decline');
        break;
      case 'processing':
        navigate('/processing');
        break;
      case 'pending':
        payType === 'vbank' || payType === 'otc'
          ? navigate('/vbankPending')
          : navigate('/pending');
        break;
      case 'failed':
        navigate('/failed');
        break;
      default:
        navigate('/error');
    }
  };

  const handlePayNow = e => {
    e.preventDefault();

    // 성(lastName) 필수유무
    if (lastName === '') {
      setNameError(true);
      openModal(ErrorModal, {
        message: t('lastName_input_error'),
      });
      return;
    }
    setNameError(false);

    // 전화번호 필수유무
    if (phoneNum === '') {
      setPhoneNumError(true);
      openModal(ErrorModal, {
        message: t('phoneNum_input_error'),
      });
      return;
    }
    setPhoneNumError(false);

    // 이메일 필수유무
    if (email === '') {
      setEmailError(true);
      openModal(ErrorModal, {
        message: t('econtext_email_input_error'),
      });
      return;
    }
    setEmailError(false);
    // 이메일 validation
    if (email !== '') {
      if (!checkEmail(email)) {
        setEmailError(true);
        openModal(ErrorModal, {
          message: t('check_email_format'),
        });
        return;
      }
      setEmailError(false);
    }

    if (isPaymentTokenExpired()) {
      // token expired
      navigate('/paymentExpired');

      return;
    }

    // globalDispatch.loadingTrue();
    const pv02Body: RequestPV02Form = {
      schemeCode: selectedScheme.schemeCd,
      amount: paymentState.requestAmount,
      authorization: `Bearer ${paymentState.auth}`,
      clientKey: paymentState.clientKey,
      currency: paymentState.requestCurrency,
      mid: paymentState.mid,
      orderId: paymentState.orderId,
      productName: paymentState.productName,
      reqDate: paymentState.reqDate,
      webhookUrl: paymentState.webhookUrl,
      returnUrl: paymentState.returnUrl,
      sign: paymentState.sign,
      firstName: firstName,
      lastName: lastName,
      email: email,
      telNo: phoneNum,
      tid: paymentState.tid,
      displayType: 'UI',
      mallReserved: paymentState.mallReserved,
    };
    setPv02RequestBody(pv02Body);
    openModal(CustomModal, {
      message: t('open_apm_app', { name: selectedScheme.schemeDisplayNm }),
      isShowTwoButton: true,
      onSubmit: () => {
        setLoading(true);
        if (isMobile) {
          navigate('/formSubmit?schemeGroup=PV02', {
            state: { formData: pv02Body },
          });
        } else {
          const popup = window.open(
            '/formSubmit?schemeGroup=PV02',
            '_blank',
            'width=700, height=850',
          );
          if (popup) {
            setTimeout(() => {
              popup?.postMessage(
                { eventType: 'formData', data: pv02Body },
                window.location.origin,
              );
            }, 1000);
          }
          checkPopupTimer = setInterval(checkPopupClosed, 1000, popup);
        }
      },
    });
  };

  function checkPopupClosed(popup) {
    if (popup && popup.closed) {
      clearInterval(checkPopupTimer);
      const ErrorKey = window.sessionStorage.getItem('isError');
      if (ErrorKey !== 'true') {
        transactResult.mutate();
      }
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Enter 키의 기본 동작을 중지시킴
    }
  };

  return (
    <>
      <div className="tab">
        <div
          id="tab-1"
          className="tab-on current"
          style={{
            height: isGlobal
              ? isMobileSize
                ? 'calc((var(--vh, 1vh) * 100) - 324px)'
                : 274
              : isMobileSize
              ? 'calc((var(--vh, 1vh) * 100) - 258px)'
              : 340,
          }}
        >
          <div className="form first">
            <div className="title">{t('customer_info_title')}</div>
            <ul className="depth-2">
              <li>
                <div className="sub-title">{t('first_name')}</div>
                <div className={'input'}>
                  <input
                    name="text"
                    type="text"
                    onChange={e => {
                      setFirstName(e.target.value);
                    }}
                    value={firstName}
                  />
                </div>
              </li>
              <li>
                <div className="sub-title">
                  {t('last_name')}
                  <span className="redstar">*</span>
                </div>
                <div className={isNameError ? 'input error' : 'input'}>
                  <input
                    name="text"
                    type="text"
                    onChange={e => {
                      setLastName(e.target.value);
                    }}
                    value={lastName}
                  />
                </div>
              </li>
            </ul>
          </div>
          <div className="form">
            <div className="title">{t('billing_address')}</div>
            <ul className="depth-1">
              <li>
                <div className="sub-title">{t('address')}</div>
                <div className={'input'}>
                  <input
                    id="address"
                    name="text"
                    type="text"
                    onChange={e => {
                      setAddress(e.target.value);
                    }}
                  />
                </div>
              </li>
            </ul>
            <ul className="depth-2">
              <li>
                <div className="sub-title">{t('city')}</div>
                <div className={'input'}>
                  <input
                    name="text"
                    type="text"
                    onChange={e => {
                      setCity(e.target.value);
                    }}
                  />
                </div>
              </li>
              <li>
                <div className="sub-title">{t('country')}</div>
                <div className={'input'}>
                  <Select
                    className="user-info-select-country"
                    classNamePrefix="user-info-select-country_"
                    options={countryList}
                    placeholder={t('selectCountry')}
                    onChange={e => {
                      if (e) {
                        setCountry(e.value);
                      }
                    }}
                  />
                </div>
              </li>
            </ul>
            <ul className="depth-2">
              <li>
                <div className="sub-title">{t('zip_code')}</div>
                <div className={'input'}>
                  <input
                    name="text"
                    type="text"
                    value={zipCode}
                    onChange={e => {
                      const value = e.target.value;
                      //숫자 외 입력 못하도록 변경
                      const isNumber = checkNumber(Number(value.slice(-1)));
                      if (isNumber) {
                        setZipCode(e.target.value);
                      }
                    }}
                  />
                </div>
              </li>
              <li>
                <div className="sub-title">{t('state')}</div>
                <div className={'input'}>
                  <input
                    name="text"
                    type="text"
                    onChange={e => {
                      setState(e.target.value);
                    }}
                  />
                </div>
              </li>
            </ul>
            <ul className="depth-1">
              <li>
                <div className="sub-title">
                  {t('phone_num')}
                  <span className="redstar">*</span>
                </div>
                <div className={isPhoneNumError ? 'input error' : 'input'}>
                  <input
                    name="phone"
                    type="text"
                    onChange={e => {
                      let value = e.target.value;
                      if (numberPattern.test(value)) {
                        const onlyNumbers = e.target.value.replace(
                          /[^0-9]/g,
                          '',
                        );
                        setPhoneNum(onlyNumbers);
                      }
                    }}
                    value={phoneNum}
                  />
                </div>
              </li>
              <li>
                <div className="sub-title">
                  {t('econtext_email')}
                  <span className="redstar">*</span>
                </div>
                <div className={isEmailError ? 'input error' : 'input'}>
                  <input
                    name="email"
                    type="email"
                    onChange={e => {
                      setEmail(e.target.value);
                    }}
                    value={email}
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
        <div className="c-section">
          <div className="box">
            <button className="all-close" onClick={openPrevious}>
              {t('previous')}
            </button>
            <button
              className="orange"
              onClick={handlePayNow}
              onKeyDown={e => handleKeyDown(e)}
            >
              {t('pay_now')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PV02Form;
